import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { resetPassword } from '../../../../services/authApi';
import * as yup from 'yup';
import './styles.css';
import back from "../../../../static/img/back.svg";
import tickVerde from "../../../../static/img/tickVerde.svg";
import CuadroTexto from "../../../../static/assets/cuadroTexto/cuadroTexto";
import LoadingScreen from "../../../../static/assets/loadingScreen/loadingScreen";

const schema = yup.object().shape({
  password: yup.string()
    .required('La contraseña es requerida')
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .matches(/[A-Z]/, 'La contraseña debe tener al menos una letra mayúscula')
    .matches(/[a-z]/, 'La contraseña debe tener al menos una letra minúscula')
    .matches(/[0-9]/, 'La contraseña debe tener al menos un número'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password'), null], 'Las contraseñas no coinciden')
    .required('La confirmación de la contraseña es requerida'),
});

const passwordRequirements = [
  { regex: /.{8,}/, label: "Debe tener al menos 8 caracteres" },
  { regex: /[A-Z]/, label: "Debe contener una letra mayúscula" },
  { regex: /[a-z]/, label: "Debe contener una letra minúscula" },
  { regex: /[0-9]/, label: "Debe contener un número" }
];

const PasswordResetForm = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { uid, token } = useParams();
  const navigate = useNavigate();

  const checkRequirement = (req) => req.regex.test(password);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);

    yup.reach(schema, 'password').validate(value)
      .then(() => setErrors(prev => ({ ...prev, password: "" })))
      .catch(err => setErrors(prev => ({ ...prev, password: err.message })));

    if (confirmPassword && value !== confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: "Las contraseñas no coinciden" }));
    } else {
      setErrors(prev => ({ ...prev, confirmPassword: "" }));
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);

    if (value !== password) {
      setErrors(prev => ({ ...prev, confirmPassword: "Las contraseñas no coinciden" }));
    } else {
      setErrors(prev => ({ ...prev, confirmPassword: "" }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await schema.validate({ password, confirmPassword }, { abortEarly: false });
      await resetPassword(uid, token, { password });
      setErrorMessage("Contraseña restablecida con éxito.");
      setTimeout(() => navigate("/login"), 2000);
    } catch (validationErrors) {
      if (validationErrors.name === "ValidationError") {
        const formattedErrors = {};
        validationErrors.inner.forEach(err => {
          formattedErrors[err.path] = err.message;
        });
        setErrors(formattedErrors);
      } else if (validationErrors.response) {
        setErrorMessage(validationErrors.response.data.error || "Error al restablecer la contraseña.");
      } else {
        setErrorMessage("Error al restablecer la contraseña. Inténtalo de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.body.classList.add('no-footer-padding');
    return () => {
      document.body.classList.remove('no-footer-padding');
    };
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="password-reset-container">
      {errorMessage && (
        <CuadroTexto
          message={errorMessage}
          onAccept={() => setErrorMessage("")}
          showCancel={false}
        />
      )}
      <button className="password-reset-back" onClick={() => navigate(-1)}>
        <img src={back} alt="Regresar" />
      </button>
      <div className="password-reset-title">Restablecer Contraseña</div>
      <form className="password-reset-form" onSubmit={handleSubmit}>
        <div className="password-reset-group">
          <label htmlFor="password">
            Nueva contraseña
          </label>
          <div className="password-reset-input-container">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="Nueva contraseña"
              required
              className={`password-reset-input ${errors.password ? 'input-error' : ''}`}
            />
            <button type="button" className="password-reset-toggle" onClick={togglePasswordVisibility}>
              {showPassword ? <AiOutlineEye /> : <AiOutlineEyeInvisible />}
            </button>
          </div>
          <ul className="password-reset-hint">
            {passwordRequirements.map((req, index) => (
              <li key={index} className={checkRequirement(req) ? "password-reset-valid" : "password-reset-invalid"}>
                {checkRequirement(req) ? (
                  <img src={tickVerde} alt="✔" className="password-reset-tick" />
                ) : (
                  "❌"
                )} {req.label}
              </li>
            ))}
          </ul>
        </div>
        <div className="password-reset-group">
          <label htmlFor="confirm-password">
            Confirmar contraseña
          </label>
          <div className="password-reset-input-container">
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirm-password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              placeholder="Confirmar contraseña"
              required
              className={`password-reset-input ${errors.confirmPassword ? 'input-error' : ''}`}
            />
          </div>
          {errors.confirmPassword && (
            <small className="password-reset-error">{errors.confirmPassword}</small>
          )}
        </div>
        <button type="submit" className="password-reset-button" disabled={errors.password || errors.confirmPassword}>
          Restablecer Contraseña
        </button>
        <div className="password-reset-link" onClick={() => navigate('/login')}>
          Regresar al inicio de sesión
        </div>
      </form>
    </div>
  );
};

export default PasswordResetForm;
